import { createContext, useContext, useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { newsletterTerms } from "@/lib/skin";
import { X, ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

const TourContext = createContext({ startTour: () => {} });
export const useTour = () => useContext(TourContext);

function buildSteps(role, features) {
  const terms = newsletterTerms();
  const done = { title: "You're all set 🎉", body: "That's the tour. Explore any screen freely — this is a read-only demo, so nothing you click will change the sample data." };
  if (role === "member") {
    return [
      { route: "/dashboard", sel: '[data-testid="nav--dashboard"]', title: "Your dashboard", body: "This is your home base — quick actions and the live status of everything you've submitted." },
      { route: "/submit/comms-request", sel: '[data-testid="nav--submit-comms-request"]', title: "Make a request", body: "Request flyers, graphics, and campaigns with a short, guided form — no more scattered emails." },
      { route: "/submissions", sel: '[data-testid="nav--submissions"]', title: "Track everything", body: "Follow each request from submitted, to approved, to published — all in one place." },
      done,
    ];
  }
  const s = [
    { route: "/admin", sel: '[data-testid="nav--admin"]', title: "Command center", body: "Every request lands here, grouped by exactly where it is in the workflow." },
    { route: "/admin", sel: '[data-testid="admin-queue-grid"]', title: "Workflow at a glance", body: "Each tile is a stage. Click one to filter — watch items move from leadership approval, to production, to published." },
    { route: "/submissions", sel: '[data-testid="nav--submissions"]', title: "All requests", body: "The full, searchable list of every request across the organization." },
  ];
  if (features.publication_builder && (role === "comms_admin" || role === "technology"))
    s.push({ route: "/issues", sel: '[data-testid="nav--issues"]', title: terms.builder, body: "Assemble approved announcements into a polished newsletter, then export it to your email platform." });
  if (features.calendar)
    s.push({ route: "/calendar", sel: '[data-testid="nav--calendar"]', title: "Shared calendar", body: "See every scheduled publication and event in one clear view." });
  if (features.advanced_reports)
    s.push({ route: "/reports", sel: '[data-testid="nav--reports"]', title: "Reports & insights", body: "Track volume, workload, and turnaround time across every team." });
  s.push({ route: "/settings", sel: '[data-testid="nav--settings"]', title: "Settings & plan", body: "Set your plan, newsletter delivery, and leadership approval rules right here." });
  s.push(done);
  return s;
}

const CARD_W = 340;

export function GuidedTour({ children }) {
  const { user, features } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [running, setRunning] = useState(false);
  const [idx, setIdx] = useState(0);
  const [rect, setRect] = useState(null);
  const steps = useMemo(
    () => (user && user.role ? buildSteps(user.role, features) : []),
    [user?.role, features.publication_builder, features.calendar, features.advanced_reports]
  );
  const step = steps[idx];
  const autoRef = useRef(false);

  const startTour = useCallback(() => {
    if (!user || !user.role) return;
    setIdx(0);
    setRunning(true);
  }, [user]);

  const finish = useCallback(() => {
    setRunning(false);
    setRect(null);
    localStorage.setItem("cc_tour_done", "1");
  }, []);

  // Auto-start once for demo visitors
  useEffect(() => {
    if (autoRef.current) return;
    if (user && user.is_demo && !localStorage.getItem("cc_tour_done")) {
      autoRef.current = true;
      const t = setTimeout(() => startTour(), 1200);
      return () => clearTimeout(t);
    }
  }, [user, startTour]);

  // Navigate to the step's screen if needed
  useEffect(() => {
    if (!running || !step) return;
    if (step.route && location.pathname !== step.route) {
      navigate(step.route);
    }
  }, [running, idx, step, location.pathname, navigate]);

  // Locate the target element (with retries for post-navigation render)
  useLayoutEffect(() => {
    if (!running || !step) return;
    if (!step.sel) { setRect(null); return; }
    let tries = 0;
    let raf;
    const find = () => {
      const el = document.querySelector(step.sel);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      } else if (tries < 25) {
        tries += 1;
        raf = setTimeout(find, 90);
      } else {
        setRect(null);
      }
    };
    find();
    return () => raf && clearTimeout(raf);
  }, [running, idx, step, location.pathname]);

  // Keep the highlight aligned on scroll/resize
  useEffect(() => {
    if (!running || !step?.sel) return;
    const update = () => {
      const el = document.querySelector(step.sel);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
      }
    };
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [running, step]);

  const next = () => (idx < steps.length - 1 ? setIdx(idx + 1) : finish());
  const prev = () => idx > 0 && setIdx(idx - 1);

  let cardStyle = {};
  if (rect) {
    const vw = window.innerWidth, vh = window.innerHeight;
    if (rect.left + rect.width + CARD_W + 32 < vw) {
      cardStyle = { top: Math.min(Math.max(rect.top, 16), vh - 240), left: rect.left + rect.width + 16 };
    } else if (rect.top + rect.height + 240 < vh) {
      cardStyle = { top: rect.top + rect.height + 16, left: Math.min(Math.max(rect.left, 16), vw - CARD_W - 16) };
    } else {
      cardStyle = { top: Math.max(rect.top - 220, 16), left: Math.min(Math.max(rect.left, 16), vw - CARD_W - 16) };
    }
  }

  return (
    <TourContext.Provider value={{ startTour }}>
      {children}
      {running && step && (
        <div className="fixed inset-0 z-[100]" data-testid="guided-tour">
          {/* Dim + spotlight */}
          {rect ? (
            <div className="pointer-events-none absolute rounded-xl transition-all duration-300"
              style={{
                top: rect.top - 6, left: rect.left - 6, width: rect.width + 12, height: rect.height + 12,
                boxShadow: "0 0 0 9999px rgba(20,28,40,0.55)",
                outline: "2px solid hsl(var(--primary))", outlineOffset: "2px",
              }} />
          ) : (
            <div className="absolute inset-0 bg-[rgba(20,28,40,0.55)]" />
          )}

          {/* Tooltip card */}
          <div
            data-testid="tour-card"
            className="absolute w-[340px] max-w-[calc(100vw-32px)] rounded-2xl border border-border bg-card p-5 shadow-2xl"
            style={rect ? { position: "fixed", ...cardStyle } : { position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)" }}
          >
            <div className="mb-2 flex items-center justify-between">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
                <Sparkles className="h-3 w-3" /> Step {idx + 1} of {steps.length}
              </span>
              <button onClick={finish} data-testid="tour-close" className="rounded-full p-1 text-muted-foreground transition hover:bg-secondary hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
            <h3 className="font-heading text-lg font-bold text-foreground">{step.title}</h3>
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
            <div className="mt-4 flex items-center justify-between">
              <button onClick={finish} data-testid="tour-skip" className="text-xs font-medium text-muted-foreground transition hover:text-foreground">Skip tour</button>
              <div className="flex items-center gap-2">
                {idx > 0 && (
                  <button onClick={prev} data-testid="tour-prev" className="flex items-center gap-1 rounded-full border border-border px-3 py-1.5 text-sm font-semibold text-foreground transition hover:bg-secondary">
                    <ArrowLeft className="h-3.5 w-3.5" /> Back
                  </button>
                )}
                <button onClick={next} data-testid="tour-next" className="flex items-center gap-1 rounded-full bg-primary px-4 py-1.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90">
                  {idx < steps.length - 1 ? <>Next <ArrowRight className="h-3.5 w-3.5" /></> : "Finish"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </TourContext.Provider>
  );
}
